import './QueryEmulator.sass'

import * as React from 'react'
import Highlight from 'react-highlight'
import { StaticQuery, graphql } from 'gatsby'
import { Link, navigate } from 'gatsby'

import UpscaleControl from '../UpscaleControl/UpscaleControl'
import CustomSwitch from '../CustomSwitch/CustomSwitch'
import QueryEmulatorInput from '../QueryEmulatorInput/QueryEmulatorInput'
import TabSwitcher from './TabSwitcher'
import { formJSON, afterImageSelector, getLabelAfterText, isMobile } from './helpers'
import Logo from '../Logo/Logo'
import { QueryEmulatorStateProps } from './'

import QuerySizesField from './QuerySizesField'
import Comparator from '../Comparator/Comparator'
import playIcon from '../../images/play-icon.svg'

class QueryEmulator extends React.Component<{}, QueryEmulatorStateProps> {
  private QueryEmulatorComparatorRef: React.RefObject<any>

  constructor(props: {}) {
    super(props)
    this.QueryEmulatorComparatorRef = React.createRef()
    this.state = {
      colorEnhancement: false,
      compressionRemoval: false,
      opacity: '100',
      opacityCopy: '0',
      upscale: false,
      upscaleValue: 'x2',
      upscaleValueCopy: '',
      upscaleHeight: 0,
      upscaleWidth: 0,
      originalWidth: 0,
      originalHeight: 0,
      isJSONActive: false,
      colorEnhancementCopy: false,
      compressionRemovalCopy: false,
      upscaleCopy: false,
      isDimmed: false,
      labelAfterText: {
        title: 'Original',
        subtitle: ''
      }
    }

    this.toggleCompressionRemoval = this.toggleCompressionRemoval.bind(this)
    this.toggleUpscale = this.toggleUpscale.bind(this)
    this.toggleColorEnhancement = this.toggleColorEnhancement.bind(this)
    this.onOpacityChange = this.onOpacityChange.bind(this)
    this.onSizeChange = this.onSizeChange.bind(this)
    this.onUpscaleChange = this.onUpscaleChange.bind(this)
    this.handleTabClick = this.handleTabClick.bind(this)
    this.handleRunQuery = this.handleRunQuery.bind(this)
    this.onImageLoaded = this.onImageLoaded.bind(this)
  }

  public toggleCompressionRemoval() {
    this.setState({ compressionRemoval: !this.state.compressionRemoval })
  }

  public toggleUpscale() {
    this.setState({ upscale: !this.state.upscale })
  }

  public toggleColorEnhancement() {
    this.setState({ colorEnhancement: !this.state.colorEnhancement })
  }

  public onOpacityChange(e: any) {
    this.setState({ opacity: e.target.value })
  }

  public onSizeChange(width: number, height: number) {
    this.setState({ 
      upscaleHeight: height,
      upscaleWidth: width
    })
  }

  public onUpscaleChange(upscaleValue: string) {
    this.setState({ upscaleValue })
  }

  public handleTabClick(value: boolean) {
    this.setState({ isJSONActive: value })
  }

  public handleRunQuery() {
    const labelAfterText = getLabelAfterText(this.state)

    this.setState({
      compressionRemovalCopy: this.state.compressionRemoval,
      colorEnhancementCopy: this.state.colorEnhancement,
      upscaleCopy: this.state.upscale,
      upscaleValueCopy: this.state.upscaleValue,
      opacityCopy: this.state.opacity,
      isDimmed: true,
      labelAfterText
    })

    if (isMobile()) {
      navigate('/demo/#query')
    }

    setTimeout(() => {
      this.setState({ isDimmed: false })
    }, 1500)
  }

  public onImageLoaded(width: number, height: number) {
    this.setState({
      originalWidth: width,
      originalHeight: height
    })
  }

  render () {
    const { compressionRemoval, upscale, upscaleValue, colorEnhancement, opacity, isDimmed, labelAfterText } = this.state
    const { upscaleWidth, upscaleHeight, originalHeight, originalWidth } = this.state

    const formedObject = formJSON(compressionRemoval, upscale, upscaleValue, colorEnhancement, opacity)
    const jsonToRender = JSON.stringify(formedObject, null, 2)

    const originalSize = (width: number, height: number) => `${width} x  ${height}`
    const imageDimmed = isDimmed ? 'dimmed' : ''

    const labelBefore = (width: number, height: number) => (
      <div className='query-comparator-before'>
        <span className='query-comparator-title'>Original</span>
        <br/>
        <span className='query-comparator-subtitle'>{originalSize(width, height)}</span>
      </div>
    )

    const labelAfter = (width: number, height: number) => (
      <div className='query-comparator-after'>
        <span className='query-comparator-title'>{labelAfterText.title}</span>
        <br/>
        <span className='query-comparator-subtitle'>{labelAfterText.subtitle || originalSize(width, height)}</span>
      </div>
    )

    const renderJSONTab = this.state.isJSONActive ?
      <div className='query-tab highlight-wrapper'>
        <Highlight className='json'>
          {`${jsonToRender}`}
        </Highlight>
      </div> :
      null

    const renderImageTab = !this.state.isJSONActive ?
      <div className='query-tab' id='query'>
        <StaticQuery
          query={query}
          render={data => {
            const before = {
              desktop: data.imageOriginal.childImageSharp.fixed,
              tablet: data.imageOriginal.childImageSharp.fixed,
              mobile: data.imageOriginalMob.childImageSharp.fixed
            }
            const widthBefore = data.imageOriginal.childImageSharp.fixed.width
            const heightBefore = data.imageOriginal.childImageSharp.fixed.height

            const after = afterImageSelector(data, this.state)

            return (
              <Comparator
                beforeImage={before}
                afterImage={after}
                className='query-comparator'
                dividerClass='custom-divider'
                afterImageClass={imageDimmed}
                beforeImageClass={imageDimmed}
                beforeLabel={labelBefore(widthBefore, heightBefore)}
                afterLabel={labelAfter(widthBefore, heightBefore)}
                onImageLoaded={this.onImageLoaded}
                ref={this.QueryEmulatorComparatorRef}
              />
            )
          }}
        />
      </div> :
      null
    
    const renderCustomSizesInputs = upscaleValue === 'custom' ? 
    (
      <div className='row'>
        <div className='size-inputs'>
          <QuerySizesField
            onSizeChange={this.onSizeChange}
            upscaleWidth={upscaleWidth}
            upscaleHeight={upscaleHeight}
            originalWidth={originalWidth}
            originalHeight={originalHeight}
          />
        </div>
      </div>
    )
    : null
 
    return (
      <div className='query-emulator'>
        <Logo wrapperClass='nav-demo' />

        <div className='query-emulator-inner'>
          <div className='query-result-navigation'>
            <TabSwitcher
              onClick={this.handleTabClick}
              isJSONActive={this.state.isJSONActive}
            />
          </div>

          <div className='query-emulator-content'>
            <aside>
              <div className='card'>
                <div className='row'>
                  <span className='card-title'>Compression removal</span>

                  <CustomSwitch
                    value={this.state.compressionRemoval}
                    onChange={this.toggleCompressionRemoval}
                  />
                </div>
              </div>

              <div className='card'>
                <div className='row'>
                  <span className='card-title'>Upscale</span>

                  <CustomSwitch
                    value={this.state.upscale}
                    onChange={this.toggleUpscale}
                  />
                </div>

                { this.state.upscale ? <>
                  <div className='row'>
                    <UpscaleControl
                      active={this.state.upscaleValue}
                      onChange={this.onUpscaleChange}
                    /> 
                  </div> 

                  {renderCustomSizesInputs}
                </>
                : null }

              </div>

              <div className='card'>
                <div className='row'>
                  <span className='card-title'>Color enhancement</span>
                  <CustomSwitch
                    value={this.state.colorEnhancement}
                    onChange={this.toggleColorEnhancement}
                  />
                </div>
                
                { this.state.colorEnhancement ?
                  <div className='row'>
                    <span className='card-subtitle'>Opacity</span>
                    <QueryEmulatorInput
                      placeholder='100'
                      value={this.state.opacity}
                      onChange={this.onOpacityChange}
                      postfix='%'
                    />
                  </div>
                : null }
              </div>

              <button className='query-button' onClick={this.handleRunQuery}>
                <object data={playIcon} className='query-button-icon' type='image/svg+xml' />
                Run query
              </button>
            </aside>

            <section className='query-result'>
              {renderJSONTab}
              {renderImageTab}
            </section>
          </div>
        </div>

        <div className='query-footer'>
          <span className='query-footer-title'>Would you like to discuss the specific case?</span>
          <Link className='btn btn-with-text btn-with-gradient gradient-full btn-learn-more' to='/apply'>Request a demo</Link>
        </div>
      </div>
    )
  }
}

const query = graphql`
  query {
    imageOriginal: file(relativePath: { eq: "demo-comparator/girl-original.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageOriginalMob: file(relativePath: { eq: "demo-comparator/girl-original-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageUpscale4x: file(relativePath: { eq: "demo-comparator/girl-upscaled-4x.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageUpscale4xMob: file(relativePath: { eq: "demo-comparator/girl-upscaled-4x-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageCompressionRemoval: file(relativePath: { eq: "demo-comparator/girl-compression.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageCompressionRemovalMob: file(relativePath: { eq: "demo-comparator/girl-compression-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor25: file(relativePath: { eq: "demo-comparator/girl-color-25.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor25Mob: file(relativePath: { eq: "demo-comparator/girl-color-25-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor50: file(relativePath: { eq: "demo-comparator/girl-color-50.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor50Mob: file(relativePath: { eq: "demo-comparator/girl-color-50-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor75: file(relativePath: { eq: "demo-comparator/girl-color-75.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor75Mob: file(relativePath: { eq: "demo-comparator/girl-color-75-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor100: file(relativePath: { eq: "demo-comparator/girl-color-100.jpg" }) {
      childImageSharp {
        fixed(width: 966, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    },
    imageColor100Mob: file(relativePath: { eq: "demo-comparator/girl-color-100-mob.jpg" }) {
      childImageSharp {
        fixed(width: 415, quality: 90) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
  }
  `

export default QueryEmulator
